/**
 * ChatInterface Component
 * Handles conversation with the AI ConversationAgent
 * Shows message history, typing indicators, and progress
 * Uses WebSocket for real-time updates
 */

import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import './ChatInterface.css';

/**
 * When the backend returns structured disambiguation options
 * (locationOptions / workplaceOptions / workplaceCategoryOptions),
 * the same text response still contains a numbered fallback list
 * (e.g. "1. Austin, TX\n2. Austin, MN..."). If we render tappable
 * cards, we should strip those numbered lines from the displayed
 * text so the user sees preamble + question only, not duplication.
 */
const stripNumberedListLines = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .split('\n')
    .filter(line => !/^\s*\d+\.\s/.test(line))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/**
 * Maps backend priority keys (e.g. "proximity_to_work") to
 * human-friendly labels with topic-specific emojis.
 * Unknown keys gracefully fall back to a Title-Cased version of
 * the key itself with a generic ⭐ prefix.
 */
const DEMOGRAPHIC_LABELS = {
  solo: { emoji: '🙋', label: 'Solo' },
  single: { emoji: '🙋', label: 'Solo' },
  partner: { emoji: '💑', label: 'With partner' },
  couple: { emoji: '💑', label: 'Couple' },
  married: { emoji: '💑', label: 'Married' },
  family: { emoji: '👨‍👩‍👧‍👦', label: 'Family' },
  retired: { emoji: '🌴', label: 'Retired' },
  student: { emoji: '🎓', label: 'Student' }
};

const formatDemographic = (key) => {
  if (!key || typeof key !== 'string') return null;
  return DEMOGRAPHIC_LABELS[key.toLowerCase()] || null;
};

const HOUSING_INTENT_LABELS = {
  rent: { emoji: '🔑', label: 'Renting' },
  buy: { emoji: '🏠', label: 'Buying' }
};

const PRIORITY_LABELS = {
  proximity_to_work: { emoji: '🏢', label: 'Near work' },
  schools: { emoji: '🏫', label: 'Schools' },
  safety: { emoji: '🛡️', label: 'Safety' },
  walkability: { emoji: '🚶', label: 'Walkable' },
  transit: { emoji: '🚇', label: 'Transit' },
  nightlife: { emoji: '🌃', label: 'Nightlife' },
  parks: { emoji: '🌳', label: 'Parks' },
  affordability: { emoji: '💰', label: 'Affordable' },
  budget: { emoji: '💰', label: 'Budget' },
  healthcare: { emoji: '🏥', label: 'Healthcare' },
  diversity: { emoji: '🌍', label: 'Diverse' },
  lifestyle_amenities: { emoji: '✨', label: 'Lifestyle' },
  coffee_culture: { emoji: '☕', label: 'Coffee scene' },
  fine_dining: { emoji: '🍷', label: 'Fine dining' },
  shopping: { emoji: '🛍️', label: 'Shopping' },
  fitness: { emoji: '💪', label: 'Fitness' },
  dog_friendly: { emoji: '🐕', label: 'Dog-friendly' },
  artsy: { emoji: '🎨', label: 'Arts scene' },
  indian_community: { emoji: '🪔', label: 'Indian community' },
  chinese_community: { emoji: '🥢', label: 'Chinese community' },
  korean_community: { emoji: '🇰🇷', label: 'Korean community' },
  japanese_community: { emoji: '🌸', label: 'Japanese community' },
  hispanic_community: { emoji: '🌶️', label: 'Hispanic community' },
  jewish_community: { emoji: '✡️', label: 'Jewish community' },
  middle_eastern_community: { emoji: '🕌', label: 'Middle Eastern' }
};

const formatPriorityLabel = (key) => {
  if (!key || typeof key !== 'string') return null;
  const known = PRIORITY_LABELS[key];
  if (known) return { emoji: known.emoji, label: known.label };
  // Unmapped key: don't render at all. Returning null here means the
  // pill is skipped in the map loop. If we see unmapped keys in
  // production logs, we add them to PRIORITY_LABELS — we do NOT
  // show a half-formatted snake_case token to the user.
  return null;
};

/** Inline SVG send icon — up arrow (matches LG's reference screenshot).
 *  Sunday polish round 3: was a paper-airplane glyph, now a clean up-arrow
 *  inside the indigo circle button to feel more "ChatGPT/iMessage modern".
 *  Uses currentColor so the button color drives it. */
const SendIcon = ({ size = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ display: 'block' }}
  >
    <line x1="12" y1="19" x2="12" y2="5" />
    <polyline points="5 12 12 5 19 12" />
  </svg>
);

const ChatInterface = ({
  onRecommendationsReady,
  onContextUpdate,
  sessionId,
  // Round 6 (LG feedback 2026-05-05): once recommendations are on screen,
  // AIChat passes recommendationsReady=true and we lock the input. The user
  // must hit "Start over" in the nav to begin a new search. Avoids the
  // confusing case where users keep typing into the chat after results are
  // shown and wonder why nothing happens.
  recommendationsReady = false
}) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  // Issue 1 (2026-05-06 evening): which disambiguation messages have had
  // their "Show N more" expander tapped. Set of message IDs. We default to
  // showing the first PILL_VISIBLE_LIMIT options; users opt in to seeing the
  // full list. Stored as a Set so add/remove is O(1).
  const [expandedOptionsById, setExpandedOptionsById] = useState(new Set());
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  // Sunday polish round 3 (2026-05-03): inputRef so we can auto-focus the
  // textbox after every AI response. LG feedback: "i shouldn't have to click
  // the box every time before typing".
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, progress]);

  // Auto-focus the input on mount and after the AI replies. Triggered when
  // the messages array grows AND we're not in a generating state. Skipped
  // while generating recommendations because the input is disabled then.
  useEffect(() => {
    if (!isLoading && !isGenerating && inputRef.current) {
      // Delay slightly so focus lands AFTER the new bubble's slide-in
      // animation finishes scrolling — otherwise mobile keyboards can
      // interrupt the scroll.
      const t = setTimeout(() => {
        try { inputRef.current && inputRef.current.focus(); } catch {}
      }, 60);
      return () => clearTimeout(t);
    }
  }, [messages, isLoading, isGenerating]);

  // Round 6 #6 (LG feedback 2026-05-05): chat history persists across
  // navigation. When user side-trips to /submit-review or /feedback and
  // returns, ChatInterface re-mounts but reads the cached messages from
  // sessionStorage instead of resetting to the greeting. Cache is keyed
  // by sessionId so different sessions don't bleed (defensive — same
  // pattern AIChat already uses for recommendations).
  //
  // On Start over (handled in AIChat.handleStartOver), the sessionStorage
  // entry is cleared and window.location.reload() runs, which fires this
  // effect again with no cache → user gets the fresh greeting.
  useEffect(() => {
    if (!sessionId) return;
    try {
      const cachedRaw = sessionStorage.getItem('ai_chat_messages');
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw);
        if (
          cached &&
          cached.sessionId === sessionId &&
          Array.isArray(cached.messages) &&
          cached.messages.length > 0
        ) {
          // Re-hydrate Date objects from JSON ISO strings — JSON.parse
          // doesn't restore Date instances automatically, but the chat
          // bubbles render `msg.timestamp.toLocaleTimeString(...)`, which
          // requires a real Date.
          const restored = cached.messages.map((m) => ({
            ...m,
            timestamp: m.timestamp ? new Date(m.timestamp) : new Date()
          }));
          setMessages(restored);
          return; // Skip the greeting — we have prior chat to continue.
        }
      }
    } catch (err) {
      console.warn('[ChatInterface] Failed to restore chat:', err.message);
    }
    // No cache (or invalid cache): show the greeting.
    setMessages([{
      id: 1,
      type: 'agent',
      text: "Hi! I'm your AI neighborhood assistant. I'll help you find the perfect place to live. Where are you looking to move?",
      timestamp: new Date()
    }]);
  }, [sessionId]);

  // Persist messages on every change — round 6 #6.
  useEffect(() => {
    if (!sessionId || messages.length === 0) return;
    try {
      sessionStorage.setItem(
        'ai_chat_messages',
        JSON.stringify({ sessionId, messages, savedAt: Date.now() })
      );
    } catch (err) {
      // Quota errors etc. — non-fatal.
      console.warn('[ChatInterface] Failed to persist chat:', err.message);
    }
  }, [messages, sessionId]);

  // Initialize WebSocket connection
  useEffect(() => {
    // Deploy night fix 2026-05-03: read socket URL from REACT_APP_API_BASE env var
    // so production points to the Render backend. Falls back to localhost for
    // local dev. socket.io-client auto-upgrades to wss:// for https URLs.
    const SOCKET_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5050';
    // Connect to Socket.io server
    const socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling']
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('✅ WebSocket connected:', socket.id);
      // Join session room
      socket.emit('join-session', sessionId);
    });

    socket.on('disconnect', () => {
      console.log('❌ WebSocket disconnected');
    });

    // Listen for recommendation progress updates
    socket.on('recommendation-progress', (data) => {
      console.log('📊 Progress update:', data);
      setProgress({
        stage: data.stage,
        message: data.message,
        progress: data.progress
      });
    });

    // Listen for recommendation completion
    socket.on('recommendation-complete', (data) => {
      console.log('✅ Recommendations complete:', data);
      setIsGenerating(false);
      setProgress(null);

      // Notify parent component
      if (onRecommendationsReady) {
        onRecommendationsReady(data);
      }

      // Add success message
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'system',
        text: `Found ${data.recommendations?.length || 0} perfect matches! Check them out below.`,
        timestamp: new Date()
      }]);
    });

    // Listen for errors
    socket.on('recommendation-error', (data) => {
      console.error('❌ Recommendation error:', data);
      setIsGenerating(false);
      setProgress(null);

      // Add error message
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        text: `Oops! Something went wrong: ${data.error}. Please try again.`,
        timestamp: new Date()
      }]);
    });

    // Cleanup on unmount
    return () => {
      socket.emit('leave-session', sessionId);
      socket.disconnect();
    };
  }, [sessionId, onRecommendationsReady]);

  // Issue 1 (2026-05-06 evening): how many disambiguation options to show
  // before requiring a "Show more" tap. Five is the sweet spot — long enough
  // to surface the right answer for most queries (Apple flagship locations,
  // common cities) without forcing the user to scroll past 18 stacked cards.
  const PILL_VISIBLE_LIMIT = 5;

  const toggleOptionsExpanded = (msgId) => {
    setExpandedOptionsById(prev => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId);
      else next.add(msgId);
      return next;
    });
  };

  // Core send logic. Shared by the text input form AND the tappable
  // disambiguation option cards. The only difference is the origin —
  // the input form passes the text the user typed; the cards pass
  // the option number (e.g. "1", "2").
  //
  // Issue 2 (2026-05-06 evening): `displayText` is an optional second arg.
  // When the user taps a disambiguation card, we want the bubble to show
  // the option's HUMAN LABEL ("Apple Park") even though the API request
  // body still carries the numeric INDEX ("1") — because the workplace
  // handler in routes/multiAgentRoutes.js (~line 253) only accepts
  // numeric indices via /^\d+$/. Decoupling display from API payload
  // keeps the chat readable without any backend change.
  const sendUserMessage = async (userMessage, displayText = null) => {
    if (!userMessage || !userMessage.trim() || isLoading || isGenerating) return;

    const trimmedMessage = userMessage.trim();
    const trimmedDisplay = (displayText || userMessage).toString().trim();

    // Add user message to chat
    const newUserMsg = {
      id: messages.length + 1,
      type: 'user',
      text: trimmedDisplay,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newUserMsg]);
    setIsLoading(true);

    try {
      // Import API dynamically to avoid circular deps
      const { sendChatMessage, startRecommendations } = await import('../../services/aiApi');

      // Send message to ConversationAgent — uses the original (numeric for
      // option-cards, free-text for the input form) message, NOT displayText.
      const chatResult = await sendChatMessage(trimmedMessage);

      if (!chatResult.success) {
        throw new Error(chatResult.error);
      }

      // Detect whether the backend returned structured disambiguation options.
      // When present, we strip the numbered-fallback list from the displayed
      // text so the user sees cards instead of a wall of text.
      const hasOptions =
        (chatResult.data.locationOptions && chatResult.data.locationOptions.length > 0) ||
        (chatResult.data.workplaceOptions && chatResult.data.workplaceOptions.length > 0) ||
        (chatResult.data.workplaceCategoryOptions && chatResult.data.workplaceCategoryOptions.length > 0);

      const displayText = hasOptions
        ? stripNumberedListLines(chatResult.data.response)
        : chatResult.data.response;

      // Add agent response (with optional structured option arrays)
      const agentMsg = {
        id: messages.length + 2,
        type: 'agent',
        text: displayText,
        timestamp: new Date(),
        completeness: chatResult.data.completeness,
        extractedData: chatResult.data.extractedData,
        locationOptions: chatResult.data.locationOptions || null,
        workplaceOptions: chatResult.data.workplaceOptions || null,
        workplaceCategoryOptions: chatResult.data.workplaceCategoryOptions || null
      };

      setMessages(prev => [...prev, agentMsg]);
      setExtractedData(chatResult.data.extractedData);

      // Sunday polish 2026-05-03: surface extracted context to parent so the
      // AIChat right panel can show a Google Maps preview as soon as the user's
      // target city is detected. The map disappears once isReadyToSearch=true
      // and recommendation cards render in its place.
      if (onContextUpdate && chatResult.data.extractedData) {
        onContextUpdate(chatResult.data.extractedData);
      }

      // Check if ready to generate recommendations
      if (chatResult.data.isReadyToSearch) {
        setIsGenerating(true);

        // Add "generating" message
        const generatingMsg = {
          id: messages.length + 3,
          type: 'system',
          text: 'Great! I have everything I need. Let me find the perfect neighborhoods for you...',
          timestamp: new Date()
        };

        setMessages(prev => [...prev, generatingMsg]);

        // Start recommendations (WebSocket will handle the rest)
        const startResult = await startRecommendations(chatResult.data.extractedData);

        if (!startResult.success) {
          throw new Error(startResult.error);
        }

        // WebSocket listeners will handle progress updates and completion
        console.log('🚀 Recommendations started, waiting for WebSocket updates...');
      }

    } catch (error) {
      console.error('Chat error:', error);

      // Add error message
      const errorMsg = {
        id: messages.length + 2,
        type: 'error',
        text: `Oops! Something went wrong: ${error.message}. Please try again.`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMsg]);
      setIsGenerating(false);
      setProgress(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Thin wrapper around sendUserMessage for the input form.
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    const userMessage = inputValue.trim();
    setInputValue('');
    await sendUserMessage(userMessage);
  };

  // Handler for when the user taps a disambiguation option card.
  // The backend (routes/multiAgentRoutes.js ~line 253) accepts the
  // option's 1-based index as a numeric string. We send "1" / "2" / etc.
  // as the API payload but pass the option's LABEL through as the
  // displayed bubble text, so the chat reads "Apple Park" instead of "1".
  const handleOptionSelect = (optionIndex, opt) => {
    if (isLoading || isGenerating) return;
    const apiPayload = String(optionIndex + 1);
    // Compose a clean display string: prefer label + sublabel separated
    // by a soft separator. Falls back to just the label, then to the API
    // payload itself (defensive — should never happen since options always
    // have a label, but harmless if the schema ever changes).
    const displayText = opt
      ? (opt.sublabel ? `${opt.label} · ${opt.sublabel}` : opt.label)
      : apiPayload;
    sendUserMessage(apiPayload, displayText);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  return (
    <div className="chat-interface">
      {/* Chat header renders only when there are captured pills to show.
          Removed the redundant "💬 AI Neighborhood Assistant" h2 — the
          page-level "LocalGenie" branding plus the visible chat surface
          make a duplicate title unnecessary. */}
      {(() => {
        // Evaluate once: do we have anything to show?
        const demographic = formatDemographic(extractedData?.demographics?.relationshipStatus);
        const housingIntent = extractedData?.housingIntent
          ? HOUSING_INTENT_LABELS[extractedData.housingIntent]
          : null;
        const hasAnyPill =
          extractedData && (
            extractedData.city ||
            extractedData.budget ||
            extractedData.children > 0 ||
            demographic ||
            housingIntent ||
            (extractedData.priorities && extractedData.priorities.length > 0)
          );
        if (!hasAnyPill) return null;

        return (
          <div className="chat-header">
            <div
              className="progress-pills"
              role="group"
              aria-label="Information captured from your conversation"
            >
              {extractedData.city && (
                <span className="pill">📍 {extractedData.city}</span>
              )}
              {demographic && (
                <span className="pill">{demographic.emoji} {demographic.label}</span>
              )}
              {extractedData.children > 0 && (
                <span className="pill">👶 {extractedData.children} kids</span>
              )}
              {housingIntent && (
                <span className="pill">{housingIntent.emoji} {housingIntent.label}</span>
              )}
              {extractedData.budget && (
                <span className="pill">
                  💰 ${extractedData.budget.toLocaleString()}
                  {extractedData.budgetType === 'monthly_rent' ? '/mo' : ''}
                </span>
              )}
              {extractedData.priorities && extractedData.priorities.length > 0 && (
                extractedData.priorities.slice(0, 4).map((priorityKey, idx) => {
                  const formatted = formatPriorityLabel(priorityKey);
                  if (!formatted) return null;
                  return (
                    <span key={`priority-${idx}-${priorityKey}`} className="pill">
                      {formatted.emoji} {formatted.label}
                    </span>
                  );
                })
              )}
            </div>
          </div>
        );
      })()}

      {/* Round 6 (LG feedback 2026-05-05): once the user has progressed past
          a disambiguation step (by tapping or typing), older option cards
          should NOT be re-clickable — they grey out. The active option group
          is the one attached to the LAST agent message; if the latest agent
          message has no options, every prior group is stale. */}
      <div className="chat-messages">
        {(() => {
          const lastAgentMsgIdx = (() => {
            for (let i = messages.length - 1; i >= 0; i--) {
              if (messages[i].type === 'agent') return i;
            }
            return -1;
          })();
          const activeOptionsMsgId = lastAgentMsgIdx >= 0 ? messages[lastAgentMsgIdx].id : null;
          return messages.map((msg) => {
          // Pick whichever option set is present (only one is populated per turn).
          // Preserves order of priority: location → workplace → workplace-category.
          const options =
            (msg.locationOptions && msg.locationOptions.length > 0 && msg.locationOptions) ||
            (msg.workplaceOptions && msg.workplaceOptions.length > 0 && msg.workplaceOptions) ||
            (msg.workplaceCategoryOptions && msg.workplaceCategoryOptions.length > 0 && msg.workplaceCategoryOptions) ||
            null;
          // Round 6: an options group is "stale" once a newer agent message has
          // arrived. Stale groups stay visible (so the user can see what they
          // chose) but become non-clickable.
          const isStale = options && msg.id !== activeOptionsMsgId;

          return (
            <div key={msg.id} className={`message message-${msg.type}`}>
              <div className="message-content">
                <p>{msg.text}</p>
                {/* Round 6 (LG feedback 2026-05-05): in-chat per-message
                    completeness bar removed. The right-panel preferences
                    card now shows a single, consolidated "% ready to search"
                    signal — having two different progress indicators (the
                    backend's per-message completeness vs. the panel's
                    field-coverage %) was confusing because they computed
                    different things and rarely agreed. The completeness
                    data is still on the message object for any future use
                    but is no longer rendered. */}
              </div>

              {/* Tappable disambiguation cards — rendered below the agent message
                  whenever the backend returns structured options. Tapping sends
                  the option's 1-based number to the backend as if the user typed it.
                  Round 6 (LG feedback 2026-05-05): older option groups (attached
                  to non-latest agent messages) render disabled + greyed out so
                  the user can't retroactively click an old answer.
                  Issue 1 (2026-05-06 evening): when options.length > 5, show only
                  the first 5 with a "Show N more" expander. Apple can return 18+
                  locations and stacking 18 cards looks bad. Per-message expanded
                  state tracked in `expandedOptionsById` so older stale groups
                  remember whether they were expanded too. */}
              {options && msg.type === 'agent' && (() => {
                const isExpanded = expandedOptionsById.has(msg.id);
                const visibleOptions = isExpanded
                  ? options
                  : options.slice(0, PILL_VISIBLE_LIMIT);
                const hiddenCount = options.length - PILL_VISIBLE_LIMIT;
                const hasOverflow = options.length > PILL_VISIBLE_LIMIT;
                return (
                  <div className={'option-cards' + (isStale ? ' option-cards-stale' : '')}>
                    {visibleOptions.map((opt, idx) => (
                      <button
                        key={`${msg.id}-opt-${idx}`}
                        type="button"
                        className="option-card"
                        onClick={() => handleOptionSelect(idx, opt)}
                        disabled={isLoading || isGenerating || isStale}
                        aria-disabled={isStale ? true : undefined}
                      >
                        <span className="option-card-number">{idx + 1}</span>
                        <span className="option-card-text">
                          <span className="option-card-label">{opt.label}</span>
                          {opt.sublabel && (
                            <span className="option-card-sublabel">{opt.sublabel}</span>
                          )}
                        </span>
                      </button>
                    ))}
                    {hasOverflow && (
                      <button
                        type="button"
                        className="option-card-more"
                        onClick={() => toggleOptionsExpanded(msg.id)}
                        aria-expanded={isExpanded}
                      >
                        {isExpanded
                          ? 'Show fewer'
                          : `Show ${hiddenCount} more`}
                      </button>
                    )}
                  </div>
                );
              })()}

              <span className="message-time">
                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          );
        });
        })()}

        {isLoading && !isGenerating && (
          <div className="message message-agent typing">
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        {isGenerating && (
          <div className="message message-system generating">
            <div className="generating-status">
              <div className="spinner"></div>
              <div className="status-text">
                {progress ? (
                  <>
                    <p>{progress.message}</p>
                    <div className="progress-bar">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${progress.progress}%` }}
                      ></div>
                    </div>
                    <div className="progress-percentage">{progress.progress}%</div>
                  </>
                ) : (
                  <>
                    <p>🔍 Initializing search...</p>
                    <div className="progress-bar">
                      <div className="progress-bar-fill" style={{ width: '5%' }}></div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {(() => {
        // Detect whether the most recent agent message has pending
        // disambiguation options. If so, nudge the user to tap a card
        // instead of typing free text. (Typing a number still works
        // as a fallback — we never block the input.)
        const lastAgentMsg = [...messages].reverse().find(m => m.type === 'agent');
        const hasPendingOptions = !!(
          lastAgentMsg && (
            (lastAgentMsg.locationOptions && lastAgentMsg.locationOptions.length > 0) ||
            (lastAgentMsg.workplaceOptions && lastAgentMsg.workplaceOptions.length > 0) ||
            (lastAgentMsg.workplaceCategoryOptions && lastAgentMsg.workplaceCategoryOptions.length > 0)
          )
        );
        // Round 6: input locks once recommendations are on screen.
        const placeholder = recommendationsReady
          ? 'Recommendations ready — click Start over to begin a new search'
          : isGenerating
            ? 'Generating recommendations...'
            : hasPendingOptions
              ? 'Tap an option above or type a number...'
              : 'Type your message...';

        const inputDisabled = isLoading || isGenerating || recommendationsReady;

        return (
          <form className="chat-input-form" onSubmit={handleSendMessage}>
            <input
              type="text"
              className="chat-input"
              placeholder={placeholder}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={inputDisabled}
              ref={inputRef}
              autoFocus
            />
            <button
              type="submit"
              className="chat-send-btn"
              disabled={!inputValue.trim() || inputDisabled}
              aria-label={isLoading ? 'Sending message' : 'Send message'}
            >
              {isLoading ? (
                <span className="chat-send-loading" aria-hidden="true" />
              ) : (
                <SendIcon size={20} />
              )}
            </button>
          </form>
        );
      })()}
    </div>
  );
};

export default ChatInterface;
